type StopDictationFn = () => void;

let activeStop: StopDictationFn | null = null;

/**
 * While dictation may run, the host component can unmount on navigation
 * before its own cleanup effect runs. Register the active session stop
 * here so a top-level route listener — or any component that owns the
 * dictation lifecycle — can always tear down the mic + speech recognition
 * on route change.
 *
 * Only one dictation session is ever active at once; registering a new
 * stop replaces the previous slot without triggering the old callback.
 * The returned disposer clears the slot and runs the stop so the
 * component's own useEffect cleanup path is enough to release the mic.
 */
export function registerActiveDictationStop(stop: StopDictationFn): () => void {
  activeStop = stop;
  return () => {
    if (activeStop !== stop) return;
    activeStop = null;
    // Must run stop after clearing the slot so any App-level route handler
    // does not call a stale callback, but we still tear down mic + recognition
    // when this hook unmounts (child cleanups can run before parent pathname
    // effects).
    try {
      stop();
    } catch {
      // ignore — dictation teardown should never block unmount
    }
  };
}

export function stopActiveDictationSession(): void {
  activeStop?.();
}

/**
 * Drop `stop` from the active slot without invoking it. Used by a session
 * that has already run its own cancel/commit teardown — the registered
 * cancel is redundant at that point and we don't want to re-fire onCancel.
 * No-op if a different session is registered.
 */
export function clearActiveDictationStop(stop: StopDictationFn): void {
  if (activeStop === stop) activeStop = null;
}
